import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex, FlexCenter, FlexColumn } from '@elementary/common-ui/components';
import { colors } from '@elementary/common-ui/style/theme';
import {
  formatShortDateTime,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Tooltip } from '@mui/material';
import { ExecutionHistoryItem } from 'shared/types/execution';

const roundUp = (number: number): number => {
  if (number % 3 === 0) {
    number += number * 0.15;
  }
  const roundedNumber = Math.ceil(number);
  return roundedNumber % 3 === 0
    ? roundedNumber
    : roundedNumber + (3 - (roundedNumber % 3));
};

const Wrapper = styled.div<{ $hasFailures: boolean }>`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  overflow-x: auto;
`;

const ChartRow = styled(Flex)<{ $hasFailures: boolean }>`
  position: relative;
  height: ${({ $hasFailures }) => ($hasFailures ? '184px' : '36px')};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray16};
  background: ${({ theme }) => theme.palette.common.white};
`;

const ChartCell = styled(Flex)`
  justify-content: center;
  z-index: 1;
  width: 72px;
`;

const ChartBarClickableArea = styled(Flex)<{ $hasFailures: boolean }>`
  justify-content: center;
  align-items: ${({ $hasFailures }) => ($hasFailures ? 'flex-end' : 'center')};
  cursor: pointer;
`;

const ChartBarSelection = styled(FlexCenter)<{
  $active: boolean;
  $hasFailures: boolean;
}>`
  display: ${({ $active }) => ($active ? 'flex' : 'contents')};
  ${({ $hasFailures }) => $hasFailures && 'align-items: flex-end'};
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.palette.common.blue4};
  background: rgba(68, 148, 249, 0.1);
  box-shadow: 0px 0px 18px 0px rgba(58, 65, 103, 0.13);
  padding: ${({ $hasFailures }) => ($hasFailures ? '0px 4px 0px 4px' : '6px')};
  ${({ $hasFailures }) => $hasFailures && 'height: 100%'};
`;

const chartBarColorMap: Record<string, string> = {
  fail: colors.red,
  warn: colors.orange,
  pass: colors.green100,
  success: colors.green100,
  error: colors.red,
  skipped: colors.gray1,
  no_data: colors.gray1,
};

const ChartBar = styled.div<{
  $status: string;
  $active: boolean;
  $failures: number | null;
  $yAxisMax: number | null;
}>`
  width: 18px;
  height: ${({ $failures, $yAxisMax }) =>
    $failures && $yAxisMax ? `${($failures / $yAxisMax) * 100}%` : '18px'};
  border-radius: 4px;
  background: ${({ $status }) => chartBarColorMap[$status]};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  cursor: pointer;
`;

const DateRow = styled(Flex)`
  margin-top: 16px;
`;

const DateCell = styled(FlexCenter)`
  font-size: 9px;
  color: ${({ theme }) => theme.palette.common.gray2};
  width: 72px;
  white-space: nowrap !important;
`;

const YAxisLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.common.gray2};
`;

const CartesianGrid = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, 1fr);

  & > div {
    border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray1};
  }

  & > div:last-child {
    border-bottom: none;
  }
`;

type Props = {
  executionHistory: ExecutionHistoryItem[];
  onExecutionHistorySelect?: (executionHistory: ExecutionHistoryItem) => void;
};

const ExecutionHistoryChart = ({
  executionHistory,
  onExecutionHistorySelect,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [selectedBarIndex, setSelectedBarIndex] = useState<number>(0);

  const sortedExecutionHistory = useMemo(
    () =>
      [...executionHistory].sort(
        (a, b) =>
          new Date(a.executionTimestamp).getTime() -
          new Date(b.executionTimestamp).getTime(),
      ),
    [executionHistory],
  );

  // Default to the newest (rightmost) execution; reset when the data set changes.
  useEffect(() => {
    setSelectedBarIndex(Math.max(0, sortedExecutionHistory.length - 1));
  }, [sortedExecutionHistory.length]);

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollLeft = wrapperRef.current.scrollWidth;
    }
  }, [sortedExecutionHistory.length]);

  useEffect(() => {
    const selectedExecution = sortedExecutionHistory[selectedBarIndex];
    if (!selectedExecution) return;

    onExecutionHistorySelect?.(selectedExecution);
  }, [onExecutionHistorySelect, sortedExecutionHistory, selectedBarIndex]);

  const { hasFailures, yAxisMax } = useMemo(() => {
    const maxFailures = executionHistory.reduce(
      (max: null | number, executionHistoryItem) => {
        if (executionHistoryItem.failures === null) return max;
        return executionHistoryItem.failures > (max ?? 0)
          ? executionHistoryItem.failures
          : max;
      },
      null,
    );

    return {
      hasFailures: maxFailures !== null,
      yAxisMax: maxFailures !== null ? roundUp(maxFailures) : null,
    };
  }, [executionHistory]);

  return (
    <Flex>
      {hasFailures && yAxisMax !== null && (
        <FlexColumn
          sx={{ mr: 2, justifyContent: 'space-between', height: '184px' }}
        >
          <YAxisLabel>{yAxisMax}</YAxisLabel>
          <YAxisLabel>{(yAxisMax / 3) * 2}</YAxisLabel>
          <YAxisLabel>{yAxisMax / 3}</YAxisLabel>
          <YAxisLabel>0</YAxisLabel>
        </FlexColumn>
      )}

      <Wrapper ref={wrapperRef} $hasFailures={hasFailures}>
        <ChartRow $hasFailures={hasFailures}>
          <>
            {sortedExecutionHistory.map((executionHistoryItem, index) => (
              <ChartCell key={executionHistoryItem.id}>
                <ChartBarClickableArea
                  $hasFailures={hasFailures}
                  onClick={() => setSelectedBarIndex(index)}
                >
                  <ChartBarSelection
                    $active={selectedBarIndex === index}
                    $hasFailures={hasFailures}
                  >
                    <Tooltip
                      placement="top"
                      title={
                        executionHistoryItem.failures
                          ? `Failed results: ${executionHistoryItem.failures}`
                          : undefined
                      }
                    >
                      <ChartBar
                        $status={executionHistoryItem.status}
                        $active={selectedBarIndex === index}
                        $failures={executionHistoryItem.failures}
                        $yAxisMax={yAxisMax}
                      />
                    </Tooltip>
                  </ChartBarSelection>
                </ChartBarClickableArea>
              </ChartCell>
            ))}

            {hasFailures && yAxisMax && (
              <CartesianGrid>
                <div />
                <div />
                <div />
              </CartesianGrid>
            )}
          </>
        </ChartRow>

        <DateRow>
          {sortedExecutionHistory.map((executionHistoryItem) => (
            <DateCell key={executionHistoryItem.id}>
              {formatShortDateTime(
                localizeDate(executionHistoryItem.executionTimestamp),
              )}
            </DateCell>
          ))}
        </DateRow>
      </Wrapper>
    </Flex>
  );
};

export default ExecutionHistoryChart;
